import {
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonModal,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  type RefresherEventDetail,
} from "@ionic/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Content } from "@/components/content";
import { ConversationListItem } from "@/components/conversation/conversation-list-item";
import { NewChatModal } from "@/components/conversation/new-chat-modal";
import { SignedQueryImage } from "@/components/molecules/signed-query-image";
import { useMe } from "@/lib/hooks/use-me";
import { usePreferredHaptics } from "@/lib/hooks/use-preferred-haptics";
import { magnifyingGlassIcon, plusIcon } from "@/lib/icons/@heroicons/react/24/outline";
import { useCreateEmployeeDirectConversation } from "@/lib/query/conversation/use-create-employee-direct-conversation";
import { useInfiniteConversations } from "@/lib/query/conversation/use-infinite-conversations";
import { queryOptions_signed_key_public_by_slug } from "@/lib/requests/file/file.get-signed-key";
import { useStatusBarHeight } from "@/utils/use-status-bar-height";

export function ConversationsPage() {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);
  const statusBarHeight = useStatusBarHeight();
  const haptics = usePreferredHaptics();
  const { isAdmin } = useMe();
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [internalSearch, setInternalSearch] = useState("");
  const organizationSlug = import.meta.env.VITE_ORGANIZATION_SLUG;

  const { conversations, loadMore, hasNextPage, isLoading, refetch } = useInfiniteConversations(
    searchText || undefined,
  );
  const createEmployeeDirectConversation = useCreateEmployeeDirectConversation();

  const isEmployee = !isAdmin;
  const showStartConversationButton = isEmployee && conversations.length === 0 && !isLoading;

  const handleStartConversation = async () => {
    haptics.impact();
    await createEmployeeDirectConversation.mutateAsync();
    await refetch();
  };

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await refetch();
    event.detail.complete();
  };

  const handleNewChat = () => {
    haptics.impact();
    setIsNewChatModalOpen(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInternalSearch(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setSearchText(value);
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <IonPage>
      <Content>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent refreshingSpinner="circles" />
        </IonRefresher>

        {/* Header section */}
        <div
          className="flex flex-col gap-4 bg-white px-5 pb-0"
          style={{ paddingTop: `calc(${statusBarHeight}px + 14px)` }}
        >
          {/* Logo and new chat button row */}
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="size-9 overflow-hidden rounded-lg">
              {organizationSlug && organizationSlug !== "boomer" ? (
                <SignedQueryImage
                  width={36}
                  height={36}
                  alt="Organization Logo"
                  className="size-full object-cover"
                  {...queryOptions_signed_key_public_by_slug([
                    "android-chrome-512x512.png",
                    { slug: import.meta.env.VITE_ORGANIZATION_SLUG },
                  ])}
                />
              ) : (
                <img
                  width={36}
                  height={36}
                  src="/heyboomer.png"
                  alt="heyboomer logo"
                  className="size-full object-cover"
                />
              )}
            </div>

            {/* New chat button - only visible to admin users */}
            {isAdmin && (
              <button
                type="button"
                onClick={handleNewChat}
                className="flex size-8 items-center justify-center rounded-full bg-brand-500 text-txt-inverted transition-opacity active:opacity-70"
              >
                <IonIcon icon={plusIcon} className="size-4" />
              </button>
            )}
          </div>

          {/* Search bar */}
          <div className="flex items-center gap-2.5 rounded-[10px] border-[1.25px] border-bordercolor-primary px-4 py-2.5">
            <IonIcon icon={magnifyingGlassIcon} className="size-5 shrink-0 text-txt-primary" />
            <input
              ref={inputRef}
              type="text"
              value={internalSearch}
              onChange={handleSearchChange}
              placeholder={t("common:search.placeholder")}
              className="flex-1 bg-transparent text-[15px] font-medium leading-[1.6] text-txt-primary outline-none placeholder:text-txt-placeholder"
            />
          </div>
        </div>

        {/* Conversation list */}
        <div className="px-4 py-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-txt-secondary">{t("common:loading")}</div>
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
              <p className="text-txt-secondary">{t("routes:conversations.empty")}</p>
              {showStartConversationButton && (
                <button
                  type="button"
                  onClick={handleStartConversation}
                  disabled={createEmployeeDirectConversation.isPending}
                  className="rounded-full bg-brand-500 px-6 py-3 text-base font-medium text-txt-inverted transition-opacity active:opacity-70 disabled:opacity-50"
                >
                  {createEmployeeDirectConversation.isPending
                    ? t("common:loading")
                    : t("routes:conversations.start-conversation")}
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-col">
              {conversations.map((conversation) => (
                <ConversationListItem key={conversation.id} conversation={conversation} />
              ))}
            </div>
          )}
        </div>

        <IonInfiniteScroll onIonInfinite={loadMore} disabled={!hasNextPage}>
          <IonInfiniteScrollContent loadingText={t("common:loading")} loadingSpinner="bubbles" />
        </IonInfiniteScroll>
      </Content>

      {/* New chat modal */}
      <IonModal
        isOpen={isNewChatModalOpen}
        onDidDismiss={() => setIsNewChatModalOpen(false)}
        initialBreakpoint={0.9}
        breakpoints={[0, 0.9]}
      >
        <NewChatModal onClose={() => setIsNewChatModalOpen(false)} />
      </IonModal>
    </IonPage>
  );
}
